import React, { useCallback, useEffect, useMemo } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import Modal from 'components/Luxkit/Modal/Modal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'

interface Props {
  bundledOffers: OfferPageState['bundledOffers']
  isDatePickerOpen: boolean
  bundleSwapModalTicker: number
}

function BundleAndSaveSwapModal({
  bundledOffers,
  isDatePickerOpen,
  bundleSwapModalTicker,
}: Props) {
  const [firstHotelOffer, secondHotelOffer] = useMemo(
    () => Object.values(bundledOffers ?? {}),
    [bundledOffers],
  )

  const {
    on: openReverseModal,
    off: closeModal,
    value: isModalOpen,
  } = useToggle(false)

  const dispatch = useOfferPageDispatch()

  const swapOrder = useCallback(() => {
    const firstOfferId = firstHotelOffer.offer?.id
    const secondOfferId = secondHotelOffer.offer?.id

    if (!firstOfferId || !secondOfferId) {
      return
    }

    if (firstHotelOffer.checkInDate || secondHotelOffer.checkInDate) {
      showSnackbar('The order of hotels has been updated. Please select your dates to continue.', 'success')
    }
    dispatch({ type: OfferPageActions.resetDates, offerId: secondOfferId })
    dispatch({ type: OfferPageActions.resetDates, offerId: firstOfferId })
    dispatch({ type: OfferPageActions.reverseBundles })
    dispatch({
      type: OfferPageActions.setPaginatorCurrentOfferId,
      currentOfferId: secondOfferId,
    })

    if (!isDatePickerOpen) {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }

    closeModal()
  }, [dispatch, firstHotelOffer, secondHotelOffer, isDatePickerOpen, closeModal])

  useEffect(() => {
    if (bundleSwapModalTicker) {
      openReverseModal()
    }
  }, [bundleSwapModalTicker, openReverseModal])

  if (!bundledOffers) return null

  return (
    <div>
      <Modal
        title="Swap order of hotels?"
        isOpen={isModalOpen}
        primaryActionText="Yes, swap order"
        secondaryActionText="Cancel"
        onPrimaryActionClick={swapOrder}
        onSecondaryActionClick={closeModal}
        onClose={closeModal}
      >
        <BodyText variant="large" colour="neutral-two">
          The new order of your bundle will be:
        </BodyText>
        <BodyText variant="large" colour="neutral-two">
          1. {secondHotelOffer.offer?.property?.name}
        </BodyText>
        <BodyText variant="large" colour="neutral-two">
          2. {firstHotelOffer.offer?.property?.name}
        </BodyText>
        <br />
        <BodyText variant="large">
          Once confirmed you will need to re-select the dates for each hotel.
        </BodyText>
      </Modal>
    </div>
  )
}

export default withOfferPageState((state) => {
  return {
    bundledOffers: state.bundledOffers,
    isDatePickerOpen: state.activeFilter === 'checkin',
    bundleSwapModalTicker: state.bundleSwapModalTicker,
  }
})(BundleAndSaveSwapModal)
